// Import React library and the checkmark icon
import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * MatchedTagInfo - Information about each matched tag
 */
interface MatchedTagInfo {
  rfidNumber: string;           // The RFID tag number
  sku: string;                  // Product SKU
  productName: string;          // Product name
  quantity: number;             // Package quantity for this tag
}

/**
 * ConfirmationModalProps - What information this success popup needs
 * This modal appears after successfully matching tags to products
 */
interface ConfirmationModalProps {
  isOpen: boolean;              // Whether this success message should be visible
  onTagMore: () => void;        // Function to call when user wants to tag more items
  onViewTags: () => void;       // Function to call when user wants to see all tags
  matchedTagsCount: number;     // How many tags were successfully matched
  productName: string;         // Name of the product the tags were matched to
  matchedTags: MatchedTagInfo[]; // Details of the matched tags
}

/**
 * TagInfoRow Component - Individual tag row (read-only)
 */
const TagInfoRow: React.FC<{
  tag: MatchedTagInfo;
}> = ({ tag }) => {

  return (
    <div className="p-4 bg-secondary-50 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          {/* Tag Number Row */}
          <div className="flex items-center">
            <span className="text-sm font-mono text-secondary-900">
              🏷️ {tag.rfidNumber}
            </span>
          </div>
          
          {/* SKU and Product Name Row */}
          <div className="flex items-center space-x-3">
            <span className="text-sm text-secondary-700 font-semibold">
              {tag.sku}
            </span>
            <span className="text-sm text-secondary-600">
              {tag.productName}
            </span>
          </div>
          
          {/* Package Quantity Row */}
          <div className="flex items-center">
            <span className="text-sm text-secondary-700">
              Package Quantity: <span className="font-medium text-primary-600">{tag.quantity}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ConfirmationModal Component
 * This shows a success message after tags are successfully matched to a product
 * It gives users two options: tag more items or view their tag list
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onTagMore,
  onViewTags,
  matchedTagsCount,
  productName,
  matchedTags
}) => {
  // If the modal shouldn't be shown, don't render anything
  if (!isOpen) return null;

  return (
    // Full-screen overlay container
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Container to center the modal */}
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Semi-transparent dark background */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        
        {/* The actual modal window - made wider to accommodate tag details */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          {/* Content area - everything centered */}
          <div className="text-center">
            {/* Success icon - green circle with checkmark */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success-100 mb-4">
              <CheckCircleIcon className="h-6 w-6 text-success-600" />
            </div>
            
            {/* Main success title */}
            <h3 className="text-lg leading-6 font-medium text-secondary-900 mb-2">
              Tags Successfully Matched!
            </h3>
            
            {/* Success details box - shows how many tags matched and to what product */}
            <div className="bg-success-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center mb-2">
                <span className="text-sm text-success-800">
                  📦 <span className="font-medium">{matchedTagsCount}</span> tag{matchedTagsCount !== 1 ? 's' : ''} matched to:
                </span>
              </div>
              <p className="text-sm text-success-900 font-medium">
                {productName}
              </p>
            </div>
          </div>

          {/* Matched Tags Section */}
          {matchedTags && matchedTags.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-secondary-900">Matched Tags</h4>
              </div>
              
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {matchedTags.map((tag) => (
                  <TagInfoRow
                    key={tag.rfidNumber}
                    tag={tag}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Prompt asking what the user wants to do next */}
          <div className="text-center mb-6">
            <p className="text-sm text-secondary-600">
              What would you like to do next?
            </p>
          </div>

          {/* Button container - three buttons */}
          <div className="flex space-x-3">
            {/* Left button: "Tag More Items" - outlined style */}
            <button
              type="button"
              onClick={onTagMore}
              className="flex-1 inline-flex justify-center rounded-md border border-secondary-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
            >
              Tag More Items
            </button>
            
            {/* Right button: "View All Tags" - filled style */}
            <button
              type="button"
              onClick={onViewTags}
              className="flex-1 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
            >
              View All Tags
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export so other components can import and use this modal
export default ConfirmationModal;